import React from 'react';
import './AiDisclosure.css';

/**
 * Always-visible AI disclosure (session-start notice per the 2026 companion
 * chatbot laws; an in-conversation re-notice fires every 3 hours in the chat
 * surface). Keep this bar present wherever a conversation can happen.
 *
 * The words never change (BRAND_SPEC §11.1). Wide screens show the full
 * notice with the crisis, privacy and terms links (the menu starts folded
 * there, so the bar is where Terms can always be found); phones show the
 * one-line version, which still says it is an AI, not Pastor Rob, not a
 * counselor, and keeps the crisis link. `compact` (phones, while a
 * conversation is being read and the header is tucked away) only tightens the
 * line's spacing: it is never hidden, dimmed or collapsed. Every link has a
 * 44 px hit area that does not change how it looks (AiDisclosure.css).
 *
 * One exception, decided by Alexi, Oct 17: on the front page's first view of
 * a visit (ChatView's REVEAL) the bar waits in its place, see-through, and
 * fades in last and slowly: from 7.1 s after the first frame, in by 8.5 s
 * (AI_NOTICE_NOT_BEFORE_MS, one number in ChatView.js; 0 keeps it on screen
 * from the first frame). The moment anyone starts (a key, a paste, a tap or
 * click anywhere, a scroll, the mic, Tab) it comes in at once, and nothing is
 * asked or sent to be written down until a frame with it fully in has been
 * painted (ChatView's sendMessage; a bar the browser still draws see-through
 * then is made visible first). A screen reader has it from the first frame.
 * Never held anywhere else: not on an ad's landing with a live question, a
 * link with words, a reload in the same tab, a new conversation, Focused
 * Study, a device under a crisis hold, nor with reduced motion or in a
 * high-contrast mode (switched on part-way, it comes in at once). The 3-hourly
 * re-notice is unchanged.
 */
function AiDisclosure({ compact = false }) {
  return (
    <div className={`ai-disclosure${compact ? ' compact' : ''}`} role="note" aria-label="About this AI">
      <div className="ai-disclosure-full">
        <div className="ai-disclosure-row">
          <span className="ai-disclosure-text">
            AI-assisted answers from Pastor Rob's own sermons. You're talking with an AI, not
            Pastor Rob himself, and not a counselor or emergency service.
          </span>
          <span className="ai-disclosure-links">
            <a href="/crisis-help" target="_blank" rel="noopener noreferrer">Crisis resources</a>
            <span aria-hidden="true"> · </span>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
            <span aria-hidden="true"> · </span>
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>
          </span>
        </div>
      </div>
      <div className="ai-disclosure-short">
        <div className="ai-disclosure-row">
          <span className="ai-disclosure-short-text">An AI, not Pastor Rob or a counselor</span>
          <span aria-hidden="true">·</span>
          <a href="/crisis-help" target="_blank" rel="noopener noreferrer">Crisis help</a>
        </div>
      </div>
    </div>
  );
}

export default AiDisclosure;
